import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { iconChevronLeft } from 'core/icons/lib/icon-chevron-left';
import { iconSliders } from 'core/icons/lib/icon-sliders';
import { RangeData } from 'core/models/range.data';
import { GenericFormControl } from 'core/utils/form-generics';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  mergeMap,
  takeUntil,
  throttleTime,
} from 'rxjs/operators';

import { StreetSearchFormGroup } from '../../../forms/street-search.form';
import { MapGeocoderResponse } from '../../../models/google-api.data';
import { MapApiService } from '../../../services/map-api.service';

@Component({
  selector: 'app-street-search-form',
  templateUrl: 'street-search-form.component.html',
  styleUrls: ['./street-search-form.component.scss'],
})
export class StreetSearchFormComponent implements OnInit, OnDestroy, OnChanges {
  arrowIcon = iconChevronLeft;
  slidersIcon = iconSliders;

  @Input()
  @HostBinding('class.input-only')
  inputOnly?: boolean;

  @ViewChild('mapSearchField')
  searchField!: ElementRef;

  @Input()
  priceRange = { min: 0, max: 100 };

  @Input() initialSearch!: string;
  @Output()
  streetSelected = new EventEmitter<{ geocoder: MapGeocoderResponse; description?: string }>();
  @Output()
  filterData = new EventEmitter<{ zones: number[]; price: RangeData; showOwn: boolean }>();

  minPriceControl = new GenericFormControl(0);
  maxPriceControl = new GenericFormControl(100);
  rangeControl = new GenericFormControl({ min: 0, max: 100 });

  form = new StreetSearchFormGroup();
  onlyOwnControl = new GenericFormControl<boolean>(false);
  showEmptyResults?: boolean;
  showMobileFilters?: boolean;
  suggestions: google.maps.places.AutocompletePrediction[] = [];

  private destroy$ = new Subject();
  private lastSelectedStreet?: MapGeocoderResponse;

  constructor(private mapsApi: MapApiService) {
    // TODO: move this logic to form
    let ignorePriceChanges = false;
    this.rangeControl.valueChanges
      .pipe(
        mergeMap(value => {
          // ignore price changes with flag to emit form change events when user change price
          // with range slider
          ignorePriceChanges = true;
          // set values without event, and then with event to price validator handle new prices
          // instead one old and one new price
          // TODO: validate min price must be less then max and max greater then min
          this.maxPriceControl.setValue(value.max, { emitEvent: false });
          this.minPriceControl.setValue(value.min, { emitEvent: false });
          ignorePriceChanges = false;
          return of(this.rangeControl.value);
        }),
        throttleTime(100, undefined, { trailing: true }),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.filterTile(value);
      });

    this.minPriceControl.valueChanges
      .pipe(
        mergeMap(value => {
          if (ignorePriceChanges) {
            return of(undefined);
          }
          this.rangeControl.setValue(
            { min: value, max: this.rangeControl.value.max },
            { emitEvent: false }
          );
          this.maxPriceControl.setValue(this.rangeControl.value.max, { emitEvent: false });
          return of(this.rangeControl.value);
        }),
        debounceTime(100),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        if (value) {
          this.filterTile(value);
        }
      });

    this.maxPriceControl.valueChanges
      .pipe(
        mergeMap(value => {
          if (ignorePriceChanges) {
            return of(undefined);
          }
          this.rangeControl.setValue(
            { min: this.rangeControl.value.min, max: value },
            { emitEvent: false }
          );
          this.minPriceControl.setValue(this.rangeControl.value.min, { emitEvent: false });
          return of(this.rangeControl.value);
        }),
        debounceTime(100),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        if (value) {
          this.filterTile(value);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges<StreetSearchFormComponent>): void {
    if (changes.initialSearch && changes.initialSearch.currentValue) {
      this.form.controls.term.setValue(changes.initialSearch.currentValue, { emitEvent: false });
    }

    if (changes.priceRange) {
      if (
        this.priceRange.min === changes.priceRange.previousValue?.min &&
        this.priceRange.max === changes.priceRange.previousValue?.max
      ) {
        return;
      }
      if (this.priceRange.min === this.priceRange.max) {
        this.rangeControl.setValue({ min: this.priceRange.min, max: this.priceRange.max });
        return;
      }
      let emitChanges = false;
      let min = this.rangeControl.value.min;
      let max = this.rangeControl.value.max;
      if (!changes.priceRange.previousValue || min === changes.priceRange.previousValue.min) {
        min = this.priceRange.min;
        emitChanges = true;
      }
      if (!changes.priceRange.previousValue || max === changes.priceRange.previousValue.max) {
        max = this.priceRange.max;
        emitChanges = true;
      }
      if (emitChanges) {
        this.rangeControl.setValue({ min, max });
      }
    }
  }

  ngOnInit(): void {
    let searchSub = new Subscription();
    this.form.controls.term.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => {
        if (!value) {
          this.lastSelectedStreet = undefined;
          this.suggestions = [];
          return;
        }
        const addressReq = this.mapsApi
          .placePredictions({ input: value, types: ['address'] })
          .pipe(map(data => data.results.filter(r => r.types.some(t => t === 'route'))));
        const citiesReq = this.mapsApi
          .placePredictions({ input: value, types: ['(regions)'] })
          .pipe(map(data => data.results));
        searchSub.unsubscribe();
        searchSub = forkJoin([addressReq, citiesReq])
          .pipe(takeUntil(this.destroy$))
          .subscribe(([addresses, cities]) => {
            cities.push(...addresses.splice(0, 10));
            this.suggestions = cities;
            this.showEmptyResults = cities.length === 0;
          });
      });
  }

  loadPlaceDetails(prediction: google.maps.places.AutocompletePrediction): void {
    this.suggestions = [];
    this.form.controls.term.setValue(prediction.description, {
      emitEvent: false,
    });
    this.mapsApi
      .geocode({ placeId: prediction.place_id })
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.lastSelectedStreet = res;
        this.streetSelected.emit({ geocoder: res, description: prediction.description });
      });
  }

  emitLastSelectedStreet(): void {
    if (!this.lastSelectedStreet) {
      return;
    }
    this.streetSelected.emit({ geocoder: this.lastSelectedStreet });
  }

  filterTile(price?: RangeData): void {
    this.applyFilter(price);
  }

  filterOwn(): void {
    this.applyFilter();
  }

  private applyFilter(price?: RangeData): void {
    const checked = this.form.levels.filter(item => item.control.value).map(i => i.zone);
    const priceValue = price ? price : this.rangeControl.value;
    this.filterData.emit({ zones: checked, price: priceValue, showOwn: this.onlyOwnControl.value });
  }
}
